<script setup lang="ts">
import { computed } from 'vue'
import MaterialPreviewSizeSelect from '@/material/web/MaterialPreviewSizeSelect.vue'
import { NButton, NModal } from 'naive-ui'
import type { MaterialPreviewSizeOption } from '@/material/web/previewUtils'

const props = defineProps<{
  show: boolean
  title: string
  caption?: string
  sizeOptions?: MaterialPreviewSizeOption[]
  selectedSize?: string
  layerUrls: string[]
  galleryUrls?: string[]
  galleryIndex?: number
}>()

const emit = defineEmits<{
  'update:show': [boolean]
  'update:selectedSize': [string]
  'update:galleryIndex': [number]
}>()

const galleryUrls = computed(() => props.galleryUrls ?? [])
const normalizedGalleryIndex = computed(() => {
  if (!galleryUrls.value.length) return 0
  return Math.min(Math.max(props.galleryIndex ?? 0, 0), galleryUrls.value.length - 1)
})
const displayLayerUrls = computed(() =>
  galleryUrls.value.length ? [galleryUrls.value[normalizedGalleryIndex.value]!] : props.layerUrls,
)
const showGalleryNav = computed(() => galleryUrls.value.length > 1)

function handleShowChange(value: boolean) {
  emit('update:show', value)
}

function moveGallery(offset: number) {
  if (!galleryUrls.value.length) return
  const next = (normalizedGalleryIndex.value + offset + galleryUrls.value.length) % galleryUrls.value.length
  emit('update:galleryIndex', next)
}
</script>

<template>
  <n-modal
    :show="show"
    class="material-preview-lightbox"
    style="width: min(1200px, 96vw)"
    :z-index="3600"
    @update:show="handleShowChange"
  >
    <section class="lightbox-shell">
      <header class="lightbox-header">
        <div class="lightbox-title-wrap">
          <div class="lightbox-title">{{ title }}</div>
          <div v-if="caption" class="lightbox-caption">{{ caption }}</div>
        </div>
        <div class="lightbox-actions">
          <MaterialPreviewSizeSelect
            :model-value="selectedSize ?? ''"
            :options="sizeOptions ?? []"
            @update:model-value="emit('update:selectedSize', String($event))"
          />
          <n-button size="small" @click="emit('update:show', false)">关闭</n-button>
        </div>
      </header>

      <div class="lightbox-stage">
        <button
          v-if="showGalleryNav"
          type="button"
          class="lightbox-nav lightbox-nav-prev"
          @click="moveGallery(-1)"
        >
          &lt;
        </button>
        <div v-if="displayLayerUrls.length" class="lightbox-canvas">
          <img
            v-for="(url, index) in displayLayerUrls"
            :key="`${url}-${index}`"
            :src="url"
            alt=""
            class="lightbox-image"
            :class="{ 'lightbox-image-overlay': index > 0 }"
          />
        </div>
        <div v-else class="lightbox-empty">暂无可预览的大图资源</div>
        <button
          v-if="showGalleryNav"
          type="button"
          class="lightbox-nav lightbox-nav-next"
          @click="moveGallery(1)"
        >
          &gt;
        </button>
      </div>
    </section>
  </n-modal>
</template>

<style scoped>
:global(.material-preview-lightbox.n-modal) {
  --n-padding-left: 0;
  --n-padding-right: 0;
  --n-padding-top: 0;
  --n-padding-bottom: 0;
}

.lightbox-shell {
  --material-font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;
  --material-font-size-base: 12px;
  --material-font-size-small: 11px;
  --material-font-size-title: 13px;
  --material-line-height: 1.35;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: min(1200px, 96vw);
  height: min(860px, 94vh);
  max-height: 94vh;
  padding: 12px;
  box-sizing: border-box;
  background: var(--theme-bg-surface);
  border: 1px solid var(--theme-border);
  font-family: var(--material-font-family);
  font-size: var(--material-font-size-base);
  line-height: var(--material-line-height);
  color: var(--theme-text-primary);
}

.lightbox-shell :deep(.n-button) {
  font-family: var(--material-font-family);
  font-size: var(--material-font-size-base);
}

.lightbox-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}

.lightbox-title-wrap {
  min-width: 0;
}

.lightbox-title {
  font-size: var(--material-font-size-title);
  font-weight: 600;
  color: var(--theme-text-heading);
}

.lightbox-caption {
  margin-top: 4px;
  font-size: var(--material-font-size-small);
  color: var(--theme-text-secondary);
  word-break: break-word;
}

.lightbox-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  flex-wrap: nowrap;
}

.lightbox-stage {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--theme-bg-panel);
  border: 1px solid var(--theme-border);
  overflow: hidden;
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  z-index: 1;
  width: 36px;
  height: 56px;
  border: 1px solid var(--theme-border);
  color: var(--theme-text-heading);
  background: var(--theme-bg-raised);
  font-size: 28px;
  line-height: 54px;
  cursor: pointer;
}

.lightbox-nav-prev {
  left: 12px;
}

.lightbox-nav-next {
  right: 12px;
}

.lightbox-canvas {
  position: relative;
  width: 100%;
  height: 100%;
}

.lightbox-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.lightbox-image-overlay {
  pointer-events: none;
}

.lightbox-empty {
  font-size: var(--material-font-size-base);
  color: var(--theme-text-secondary);
}
</style>
